/**
 * EventDetail.jsx
 *
 * Renders a single event by ID. If the event is gone (HTTP 410) or never
 * existed (HTTP 404), we render a tombstone page with <meta name="robots"
 * content="noindex, nofollow"> so Googlebot drops the URL from its index.
 *
 * Background: prior to this route, /events/<uuid> fell through to the catch-all
 * <Route path="*"> which navigated to "/", which Google Search Console then
 * flagged as "Page with redirect". A real route + noindex is the SEO-correct
 * Option A ("HTTP 410 Gone") equivalent for a single-page app.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { Calendar, MapPin, Users, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventDetail() {
  const { eventId } = useParams();
  const [state, setState] = useState({ loading: true, event: null, status: null });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/events/${eventId}`)
      .then((res) => {
        if (!cancelled) setState({ loading: false, event: res.data, status: 200 });
      })
      .catch((err) => {
        if (!cancelled) {
          const status = err?.response?.status ?? 404;
          setState({ loading: false, event: null, status });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  if (state.loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" data-testid="event-detail-loading">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Tombstone — 410 Gone or 404 Not Found
  if (!state.event) {
    const isGone = state.status === 410;
    return (
      <div className="max-w-2xl mx-auto px-4 py-16" data-testid="event-detail-tombstone">
        <SEO
          title={isGone ? 'Event Has Ended' : 'Event Not Found'}
          description="This event is no longer available. Browse upcoming events from the All American Dog Club community."
          url={`/events/${eventId}`}
          noindex={true}
        />
        <Card>
          <CardContent className="p-8 text-center space-y-5">
            <div className="text-6xl">📅</div>
            <h1 className="text-2xl font-bold" data-testid="event-tombstone-title">
              {isGone ? 'This event has ended' : 'Event not found'}
            </h1>
            <p className="text-muted-foreground">
              {isGone
                ? 'The event you\u2019re looking for has concluded and is no longer listed.'
                : 'We couldn\u2019t find that event. It may have been removed or the link may be incorrect.'}
            </p>
            <div className="pt-2">
              <Link to="/events">
                <Button data-testid="event-tombstone-back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  See upcoming events
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Live event — minimal read-only detail. Full editor UX lives at /events.
  const e = state.event;
  const startsAt = e.starts_at ? new Date(e.starts_at) : null;
  return (
    <div className="max-w-3xl mx-auto px-4 py-10 space-y-6" data-testid="event-detail-page">
      <SEO
        title={e.title || 'Event'}
        description={e.description || `Join us for ${e.title} on AADC.`}
        url={`/events/${eventId}`}
        type="event"
        event={{
          name: e.title,
          description: e.description,
          startDate: e.starts_at,
          endDate: e.ends_at,
          location: e.location,
          isOnline: !!e.is_online,
          locationUrl: e.location_url,
        }}
      />
      <Link to="/events" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4 mr-1" /> All events
      </Link>
      <h1 className="text-3xl font-bold" data-testid="event-detail-title">{e.title}</h1>
      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
        {startsAt && (
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-4 h-4" /> {format(startsAt, 'PPP p')}
          </span>
        )}
        {e.location && (
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="w-4 h-4" /> {e.location}
          </span>
        )}
        {typeof e.attendees_count === 'number' && (
          <span className="inline-flex items-center gap-1.5">
            <Users className="w-4 h-4" /> {e.attendees_count} attending
          </span>
        )}
      </div>
      {e.description && (
        <p className="whitespace-pre-wrap text-base leading-relaxed" data-testid="event-detail-description">
          {e.description}
        </p>
      )}
    </div>
  );
}
